import { useContext, useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import { FirebaseError } from 'firebase/app';
import { sendPasswordResetEmail } from 'firebase/auth';
import { MetadataError } from '@ep/error-handling';
import { BodyText, Subheading } from '../../shared/typography/PrimaryTypography';
import PrimaryButton from '../../shared/button/PrimaryButton';
import { auth } from '../../../lib/google/firebase';
import { FeedbackSnackbarContext } from '../../../context/FeedbackSnackbarContext';
import { generateUuidV4 } from '../../../util/uuid';
import { log } from '../../../util/log';
import { FUNCTION_UNKNOWN_FAILURE } from '../../../util/global_variables';

const SOURCE = 'LinkAccountsPassword.tsx';

interface LinkAccountsPasswordProps {
  email: string;
  returnToPassword: () => void;
}

/**
 * Handles the password setup step for users migrating from Microsoft SSO to email/password authentication.
 * Sends a password reset email on mount and shows a confirmation message.
 * @param {string} email The email address of the user requiring password setup.
 * @param {() => void} returnToPassword Navigates back to the login identifier step.
 * @returns {JSX.Element}
 */
const LinkAccountsPassword = ({
  email,
  returnToPassword
}: LinkAccountsPasswordProps): JSX.Element => {
  const { setFeedbackData } = useContext(FeedbackSnackbarContext);
  const [sendLoad, setSendLoad] = useState<boolean>(true);
  const [sentMessage, setSentMessage] = useState<string>('');

  useEffect(() => {
    const sendResetEmail = async (): Promise<void> => {
      try {
        await sendPasswordResetEmail(auth, email);
        setSentMessage(
          `A password setup link has been sent to ${email}. Check your inbox and follow the link to create your password, then return here to sign in.`
        );
      } catch (err: unknown) {
        if ((err as FirebaseError).code === 'auth/too-many-requests') {
          setSentMessage(
            `A password setup link was recently sent to ${email}. Please check your inbox.`
          );
        } else {
          const tracking_id: string = generateUuidV4();
          log(
            'error',
            new MetadataError(
              err instanceof Error
                ? err.message
                : FUNCTION_UNKNOWN_FAILURE(SOURCE, 'sendResetEmail'),
              { email },
              tracking_id
            )
          );
          setFeedbackData({
            message: `Failed to send setup email. Tracking ID: ${tracking_id}`,
            state: true,
            type: 'error'
          });
        }
      } finally {
        setSendLoad(false);
      }
    };
    sendResetEmail();
  }, []);

  return (
    <Box>
      <Subheading weight="Medium" color="primary" sx={{ textAlign: 'center', mb: 4 }}>
        Password Setup Required
      </Subheading>
      {sendLoad ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <BodyText sx={{ my: 2 }}>
            Your organisation has moved from Microsoft sign-in to email and password. Your account (
            <strong>{email}</strong>) does not yet have a <strong>Syntiro</strong> password set.
          </BodyText>
          <BodyText sx={{ my: 2 }}>{sentMessage}</BodyText>
          <PrimaryButton sx={{ mt: 4, width: '100%' }} onClick={returnToPassword}>
            BACK TO SIGN IN
          </PrimaryButton>
        </>
      )}
    </Box>
  );
};

export default LinkAccountsPassword;
